import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useEntity } from '../../../hooks/useEntity';
import { Organization } from '../../../types/contacts';
import { normalizeWebsiteUrl } from '../../../lib/validation';
import { updateOrganizationRecord } from '../../../lib/supabase/contacts';

interface EditOrganizationFormProps {
  organization: Organization;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditOrganizationForm({ organization, onClose, onSuccess }: EditOrganizationFormProps) {
  const { entity } = useEntity();
  const [formData, setFormData] = useState({
    name: organization.name || '',
    type: organization.type || 'client',
    domain: organization.domain || ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateForm = () => {
    setValidationError(null);

    if (!formData.name.trim()) {
      setValidationError('Organization name is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!entity?.id) {
      setError('No organization found. Please try again or refresh the page.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const domain = formData.domain ? normalizeWebsiteUrl(formData.domain) : '';

      await updateOrganizationRecord(organization.id, entity.id, {
        name: formData.name.trim(),
        type: formData.type as 'client' | 'supplier' | 'partner',
        domain: domain
      });

      onSuccess();
    } catch (error) {
      console.error('Failed to update organization:', error);
      setError('Failed to update organization. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Edit Organization</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {validationError && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm mb-4">
            {validationError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Organization Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
            >
              <option value="client">Client</option>
              <option value="internal">Your Company (Internal)</option>
              <option value="supplier">Supplier</option>
              <option value="partner">Partner</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Website or Domain
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
              className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2"
              placeholder="example.com"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Updating...
                </>
              ) : 'Update Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}